<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeMinus from "@dsk/icons/LeMinus.vue";
import LePlus from "@dsk/icons/LePlus.vue";
import { computed, h, onMounted, ref, watch } from "vue";

import { useArticleInject } from "@/composables";
import { DictionariesHelper, NgCompileHelper } from "@/helpers";
import { useArticleFeedbackModalStore, useDictionariesStore, useUserStore } from "@/stores";

const props = defineProps<{
  methodId: string;
  html: string;
  index: number;
}>();

const emit = defineEmits<{
  (event: "expand"): void;
}>();

const { openFeedbackModal } = useArticleFeedbackModalStore();
const {
  injectDictionaryCover,
  injectFeedbackButton,
  injectDictionaryInfoButton,
  injectExpandToggle,
  injectCompactFooter
} = useArticleInject("le-ng-article-compact");

const dictionariesStore = useDictionariesStore();
const userStore = useUserStore();

const article = ref<HTMLElement>();
const isRendered = ref(false);

const isExpanded = computed<boolean>(() => userStore.userData.settings.compactArticlesExpanded);

const isCompanyMethod = computed<boolean>(() => {
  const dictionary = dictionariesStore.getDictionaryByMethodId(props.methodId);
  if (!dictionary) {
    return false;
  }

  return DictionariesHelper.isCompanyDictionary(dictionary);
});

const hasGyldendalExpandButtons = computed<boolean>(() => {
  if (!article.value) {
    return false;
  }

  return article.value.querySelectorAll("button[tg-expand-article-toggle]").length > 0;
});

const method = computed(() => dictionariesStore.getMethodByMethodId(props.methodId));
const dictionary = computed(() => dictionariesStore.getDictionaryByMethodId(props.methodId));

const fixGyldendalExpandToggle = (elements: HTMLElement): void => {
  elements.querySelectorAll("button[tg-expand-article-toggle]").forEach((button) => {
    button.setAttribute("method-id", props.methodId);
    if (isExpanded.value) {
      button.setAttribute("expanded-as-default", "true");
    } else {
      button.setAttribute("force-collapsed-as-default", "true");
    }
  });

  watch(
    () => isExpanded.value,
    (value) => {
      if (!article.value) {
        return;
      }

      article.value.querySelectorAll("div[tg-expandable-article]").forEach((div) => {
        // Each article has tg-expandable-article directive, so we need to find the one that is displayed
        if (div.classList.contains("hidden")) {
          return;
        }

        const button = div.querySelector("button[tg-expand-article-toggle]") as HTMLButtonElement;
        if (!button) {
          return;
        }

        const isCollapsed = button.children.length === 1 && button.children[0].classList.contains("icon-plus");

        // If the article state is the same as the general state, we need to toggle the article. If not, they
        // will simply align after the general state has changed, since we only have two states
        if (value === isCollapsed) {
          button.setAttribute("data-skip-event", "true");
          button.click();

          // Without the timeout, some events may be triggered, which we want to avoid
          setTimeout(() => {
            button.removeAttribute("data-skip-event");
          }, 1000);
        }
      });
    }
  );
};

const decorateArticleHeader = (header: Element): void => {
  if (!dictionary.value) {
    return;
  }

  injectDictionaryCover(header, dictionary.value);

  if (!isCompanyMethod.value) {
    injectFeedbackButton(header, () => {
      if (!dictionary.value) {
        return;
      }

      openFeedbackModal(props.html, dictionary.value, props.methodId);
    });
  }

  injectDictionaryInfoButton(header, dictionary.value);
};

const decorateArticleBody = (body: Element): void => {
  body.classList.add("article-fader-container");
  body.classList.add("article-fader");

  const isFirstCompanyArticle = isCompanyMethod.value && props.index === 0;
  const isArticleExpanded = ref(isFirstCompanyArticle || isExpanded.value ? true : false);

  watch(
    () => isExpanded.value,
    (value) => {
      isArticleExpanded.value = value;

      if (isArticleExpanded.value) {
        body.classList.remove("article-fader");
      } else {
        body.classList.add("article-fader");
      }
    }
  );

  if (isArticleExpanded.value) {
    body.classList.toggle("article-fader");

    if (!isExpanded.value) {
      emit("expand");
    }
  }

  const iconButton = h(
    LeIconButton,
    {
      size: "l",
      onClick: () => {
        body.classList.toggle("article-fader");
        isArticleExpanded.value = !isArticleExpanded.value;

        if (isArticleExpanded.value) {
          emit("expand");
        }
      }
    },
    { default: () => (isArticleExpanded.value ? h(LeMinus) : h(LePlus)) }
  );

  injectExpandToggle(body, iconButton);
};

const decorateArticleFooter = (footer: Element): void => {
  if (!method.value || !dictionary.value) {
    return;
  }

  injectCompactFooter(footer, method.value, dictionary.value);
};

onMounted(() => {
  watch(
    () => props.html,
    () => {
      if (!article.value) {
        return;
      }

      const headerIndex = article.value.children[0].classList.contains("article-prefix") ? 1 : 0;
      const bodyIndex = headerIndex + 1;

      if (hasGyldendalExpandButtons.value) {
        fixGyldendalExpandToggle(article.value);
      }

      decorateArticleHeader(article.value.children[headerIndex]);

      // Some articles doesn't have a body
      if (!hasGyldendalExpandButtons.value && article.value.children[bodyIndex]) {
        decorateArticleBody(article.value.children[bodyIndex]);
      }

      decorateArticleFooter(article.value.children[article.value.children.length - 1]);

      NgCompileHelper.compileNgTemplate(article.value, props.index === 0);
      isRendered.value = true;
    },
    { immediate: true }
  );
});
</script>

<template>
  <article
    v-bind="$attrs"
    ref="article"
    class="le-ng-article-compact"
    :class="{ 'le-ng-article-compact__hidden': !isRendered }"
    v-html="props.html"
  ></article>
</template>

<style scoped lang="less">
@import "@dsk/styles/all";
@import "@/assets/styles/z-index";

.le-ng-article-compact {
  &__hidden {
    display: none;
  }

  ::v-deep(.article-headline) {
    position: relative;
    min-height: 62px;
    padding: 16px 90px 16px 60px;
  }

  ::v-deep(.article-fader-container) {
    position: relative;
    margin-left: 0;
    padding-left: 60px;
  }

  ::v-deep(.article-fader) {
    height: 75px;
    overflow-y: hidden;
    mask-image: linear-gradient(white 60%, transparent);
  }

  ::v-deep(.chemical-article-content) {
    padding-right: 140px;
  }

  ::v-deep(.chemical-article-headline td) {
    padding-left: 20px;
  }

  ::v-deep(.chemical-article-headline table) {
    position: static;
  }

  ::v-deep(.cent-dada-article-content) {
    padding-left: 0;
  }

  ::v-deep(.company-article-content) {
    padding-top: 12px;
  }

  ::v-deep(.dsn-article-content) {
    padding-top: 24px;
  }

  ::v-deep(.gyldendal-article-branding) {
    display: none;
  }

  ::v-deep(.gyldendal-article-content-wrapper) {
    max-width: 100%;

    #media-breakpoint-up(md, {
      .gyldendal-article-content {
        margin-left: -25px;
      }
    });

    .plus-button,
    .minus-button {
      display: inline-block;
      width: 28px;
      height: 28px;
      margin-left: -8px;
      padding: 8px 14px !important;
      transition: all 75ms ease-out;
      border: none;
      border-radius: 50%;
      background: #edfbf7;
      box-shadow: none;

      &:hover {
        background: #c9f5e8;
      }

      &:focus {
        box-shadow: 0 0 0 4px rgba(27, 190, 163, 0.3);
      }

      .icon {
        color: #00a489;
      }
    }
  }

  ::v-deep(&__cover) {
    position: absolute;
    top: 8px;
    left: 16px;
  }

  ::v-deep(&__feedback) {
    position: absolute;
    top: 18px;
    right: 60px;
  }

  ::v-deep(&__information) {
    position: absolute;
    top: 18px;
    right: 16px;
  }

  ::v-deep(&__expand-toggle) {
    position: absolute;
    top: 20px;
    left: 16px;
  }
}
</style>
